// src/api/cajas.js - API para manejo de cajas
import { apiFetch, API_CONFIG } from './index.js';

/**
 * Obtener todas las cajas
 * @param {string} estado - 'all', 'abiertas', 'cerradas'
 * @returns {Promise} Lista de cajas
 */
export const obtenerCajas = async (estado = 'all') => {
  try {
    console.log('📋 Obteniendo cajas...', { estado });
    
    let endpoint = '/Cajas';
    
    // Usar endpoint específico según el estado
    if (estado === 'abiertas') {
      endpoint = '/Cajas/abiertas';
    } else if (estado === 'cerradas') {
      endpoint = '/Cajas/cerradas';
    } else if (estado && estado !== 'all') {
      endpoint = `/Cajas/estado/${estado}`;
    }
    
    const response = await apiFetch(endpoint);
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Cajas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo cajas:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener una caja por ID
 * @param {number} id - ID de la caja
 * @returns {Promise} Datos de la caja
 */
export const obtenerCajaPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de caja es requerido');
    }
    
    console.log(`🔍 Obteniendo caja ID: ${id}`);
    
    const response = await apiFetch(`/Cajas/${id}`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Caja obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo caja ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener resumen de caja por ID
 * @param {number} id - ID de la caja
 * @returns {Promise} Resumen de la caja
 */
export const obtenerResumenCaja = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de caja es requerido');
    }
    
    console.log(`📊 Obteniendo resumen caja ID: ${id}`);
    
    const response = await apiFetch(`/Cajas/${id}/resumen`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Resumen obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo resumen caja ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener caja abierta por usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise} Caja abierta del usuario
 */
export const obtenerCajaAbiertaPorUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) {
      throw new Error('ID de usuario es requerido');
    }
    
    console.log(`🔍 Obteniendo caja abierta para usuario: ${idUsuario}`);
    
    const response = await apiFetch(`/Cajas/usuario/${idUsuario}/abierta`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Caja abierta obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo caja abierta usuario ${idUsuario}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener cajas por usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise} Lista de cajas del usuario
 */
export const obtenerCajasPorUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) {
      throw new Error('ID de usuario es requerido');
    }
    
    console.log(`👤 Obteniendo cajas por usuario: ${idUsuario}`);
    
    const response = await apiFetch(`/Cajas/usuario/${idUsuario}`);
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Cajas del usuario obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo cajas usuario ${idUsuario}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener cajas por rango de fechas
 * @param {Date} fechaInicio - Fecha de inicio
 * @param {Date} fechaFin - Fecha de fin
 * @returns {Promise} Lista de cajas en el rango
 */
export const obtenerCajasPorRangoFechas = async (fechaInicio, fechaFin) => {
  try {
    if (!fechaInicio || !fechaFin) {
      throw new Error('Fechas de inicio y fin son requeridas');
    }
    
    console.log('📅 Obteniendo cajas por rango de fechas...', { fechaInicio, fechaFin });
    
    const params = new URLSearchParams({
      fechaInicio: fechaInicio.toISOString().split('T')[0],
      fechaFin: fechaFin.toISOString().split('T')[0]
    });
    
    const response = await apiFetch(`/Cajas/fechas?${params}`);
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Cajas por fechas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo cajas por fechas:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener total actual en cajas abiertas
 * @returns {Promise} Total actual
 */
export const obtenerTotalCajaActual = async () => {
  try {
    console.log('💰 Obteniendo total actual de cajas...');
    
    const response = await apiFetch('/Cajas/total-actual');
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Total obtenido exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo total actual:', error);
    return {
      success: false,
      error: error.message,
      data: 0
    };
  }
};

/**
 * Crear una nueva caja
 * @param {Object} cajaData - Datos de la caja
 * @returns {Promise} Caja creada
 */
export const crearCaja = async (cajaData) => {
  try {
    // Validar datos requeridos
    if (cajaData.montoInicial === undefined || cajaData.montoInicial < 0) {
      throw new Error('El monto inicial es requerido y no puede ser negativo');
    }
    
    if (!cajaData.idUsuario) {
      throw new Error('El ID de usuario es requerido');
    }
    
    console.log('➕ Creando nueva caja:', cajaData);
    
    const response = await apiFetch('/Cajas', {
      method: 'POST',
      body: JSON.stringify({
        montoInicial: parseFloat(cajaData.montoInicial),
        idUsuario: parseInt(cajaData.idUsuario),
        observaciones: cajaData.observaciones || '',
        estado: cajaData.estado || 'Abierta'
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Caja creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando caja:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Abrir una nueva caja
 * @param {Object} cajaData - Datos para abrir caja
 * @returns {Promise} Caja abierta
 */
export const abrirCaja = async (cajaData) => {
  try {
    // Validar datos requeridos
    if (cajaData.montoInicial === undefined || cajaData.montoInicial < 0) {
      throw new Error('El monto inicial es requerido y no puede ser negativo');
    }
    
    if (!cajaData.idUsuario) {
      throw new Error('El ID de usuario es requerido');
    }
    
    console.log('🚪 Abriendo nueva caja:', cajaData);
    
    const response = await apiFetch('/Cajas/abrir', {
      method: 'POST',
      body: JSON.stringify({
        montoInicial: parseFloat(cajaData.montoInicial),
        idUsuario: parseInt(cajaData.idUsuario),
        observaciones: cajaData.observaciones || '',
        estado: cajaData.estado || 'Abierta'
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Caja abierta exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error abriendo caja:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cerrar una caja existente
 * @param {number} id - ID de la caja
 * @param {Object} cierreData - Datos para cerrar caja
 * @returns {Promise} Caja cerrada
 */
export const cerrarCaja = async (id, cierreData) => {
  try {
    if (!id) {
      throw new Error('ID de caja es requerido');
    }
    
    if (cierreData.montoFinal === undefined || cierreData.montoFinal < 0) {
      throw new Error('El monto final es requerido y no puede ser negativo');
    }
    
    console.log(`🔒 Cerrando caja ID: ${id}`, cierreData);
    
    const response = await apiFetch(`/Cajas/${id}/cerrar`, {
      method: 'POST',
      body: JSON.stringify({
        montoFinal: parseFloat(cierreData.montoFinal),
        totalVentas: parseFloat(cierreData.totalVentas || 0),
        totalServicios: parseFloat(cierreData.totalServicios || 0),
        totalVentasDolares: parseFloat(cierreData.totalVentasDolares || 0),
        totalServiciosDolares: parseFloat(cierreData.totalServiciosDolares || 0),
        observaciones: cierreData.observaciones || ''
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Caja cerrada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error cerrando caja ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar una caja existente
 * @param {number} id - ID de la caja
 * @param {Object} cajaData - Datos a actualizar
 * @returns {Promise} Caja actualizada
 */
export const actualizarCaja = async (id, cajaData) => {
  try {
    if (!id) {
      throw new Error('ID de caja es requerido');
    }
    
    console.log(`✏️ Actualizando caja ID: ${id}`, cajaData);
    
    const updateData = {};
    
    if (cajaData.montoFinal !== undefined) updateData.montoFinal = parseFloat(cajaData.montoFinal);
    if (cajaData.totalVentas !== undefined) updateData.totalVentas = parseFloat(cajaData.totalVentas);
    if (cajaData.totalServicios !== undefined) updateData.totalServicios = parseFloat(cajaData.totalServicios);
    if (cajaData.totalVentasDolares !== undefined) updateData.totalVentasDolares = parseFloat(cajaData.totalVentasDolares);
    if (cajaData.totalServiciosDolares !== undefined) updateData.totalServiciosDolares = parseFloat(cajaData.totalServiciosDolares);
    if (cajaData.estado !== undefined) updateData.estado = cajaData.estado;
    if (cajaData.observaciones !== undefined) updateData.observaciones = cajaData.observaciones;
    
    const response = await apiFetch(`/Cajas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Caja actualizada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando caja ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar una caja
 * @param {number} id - ID de la caja a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarCaja = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de caja es requerido');
    }
    
    console.log(`🗑️ Eliminando caja ID: ${id}`);
    
    const response = await apiFetch(`/Cajas/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Caja eliminada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando caja ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de una caja
 * @param {number} id - ID de la caja
 * @param {string} estado - Nuevo estado
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoCaja = async (id, estado) => {
  try {
    if (!id || !estado) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de caja ID: ${id} a ${estado}`);
    
    const response = await apiFetch(`/Cajas/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({
        estado: estado
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado de caja actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de caja ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener todos los estados disponibles
 * @returns {Promise} Lista de estados
 */
export const obtenerEstadosCajas = async () => {
  try {
    console.log('📊 Obteniendo estados de cajas...');
    
    const response = await apiFetch('/Cajas/estados');
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estados obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo estados:', error);
    return {
      success: false,
      error: error.message,
      data: ['Abierta', 'Cerrada'] // Valores por defecto
    };
  }
};

/**
 * Buscar cajas con filtros
 * @param {Object} filtros - Filtros de búsqueda
 * @returns {Promise} Cajas filtradas
 */
export const buscarCajas = async (filtros = {}) => {
  try {
    console.log('🔎 Buscando cajas con filtros:', filtros);
    
    const params = new URLSearchParams();
    
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.idUsuario) params.append('idUsuario', filtros.idUsuario);
    if (filtros.fechaDesde) params.append('fechaDesde', filtros.fechaDesde);
    if (filtros.fechaHasta) params.append('fechaHasta', filtros.fechaHasta);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/Cajas/buscar?${queryString}` : '/Cajas/buscar';
    
    const response = await apiFetch(endpoint);
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Búsqueda completada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error buscando cajas:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

// ==================== FUNCIONES AUXILIARES (NO API) ====================

/**
 * Formatear fecha de caja
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaCaja = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
      opciones.second = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Calcular duración de caja
 * @param {string} fechaApertura - Fecha de apertura
 * @param {string} fechaCierre - Fecha de cierre
 * @returns {string} Duración formateada
 */
export const calcularDuracionCaja = (fechaApertura, fechaCierre) => {
  if (!fechaApertura) return 'N/A';
  
  try {
    const apertura = new Date(fechaApertura);
    const cierre = fechaCierre ? new Date(fechaCierre) : new Date();
    
    const diferenciaMs = cierre.getTime() - apertura.getTime();
    const horas = Math.floor(diferenciaMs / (1000 * 60 * 60));
    const minutos = Math.floor((diferenciaMs % (1000 * 60 * 60)) / (1000 * 60));
    const segundos = Math.floor((diferenciaMs % (1000 * 60)) / 1000);
    
    if (horas > 0) {
      return `${horas}h ${minutos}m`;
    } else if (minutos > 0) {
      return `${minutos}m ${segundos}s`;
    } else {
      return `${segundos}s`;
    }
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado de la caja
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

/**
 * Convertir estado a booleano
 * @param {string} estado - Estado como string
 * @returns {boolean} Si está abierta
 */
export const esCajaAbierta = (estado) => {
  return estado?.toLowerCase() === 'abierta';
};

/**
 * Formatear monto monetario
 * @param {number} monto - Monto a formatear
 * @param {string} moneda - Símbolo de moneda
 * @returns {string} Monto formateado
 */
export const formatearMonto = (monto, moneda = 'Bs.') => {
  if (monto === null || monto === undefined) return `${moneda} 0.00`;
  
  const formatter = new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${moneda} ${formatter.format(monto)}`;
};

/**
 * Calcular total de caja
 * @param {Object} caja - Objeto de caja
 * @returns {number} Total calculado
 */
export const calcularTotalCaja = (caja) => {
  const totalVentas = parseFloat(caja.totalVentas || 0);
  const totalServicios = parseFloat(caja.totalServicios || 0);
  const montoInicial = parseFloat(caja.montoInicial || 0);
  
  return totalVentas + totalServicios + montoInicial;
};

/**
 * Calcular diferencia de caja
 * @param {Object} caja - Objeto de caja
 * @returns {number} Diferencia calculada
 */
export const calcularDiferenciaCaja = (caja) => {
  const montoInicial = parseFloat(caja.montoInicial || 0);
  const montoFinal = parseFloat(caja.montoFinal || 0);
  const totalVentas = parseFloat(caja.totalVentas || 0);
  const totalServicios = parseFloat(caja.totalServicios || 0);
  
  const totalEsperado = montoInicial + totalVentas + totalServicios;
  return montoFinal - totalEsperado;
};

/**
 * Formatear observaciones (cortar si es muy larga)
 * @param {string} observaciones - Observaciones de la caja
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Observaciones formateadas
 */
export const formatearObservaciones = (observaciones, maxLength = 100) => {
  if (!observaciones || observaciones.trim() === '') return 'Sin observaciones';
  if (observaciones.length <= maxLength) return observaciones;
  return observaciones.substring(0, maxLength) + '...';
};

/**
 * Filtrar cajas por estado
 * @param {Array} cajas - Lista de cajas
 * @param {string} estado - 'all', 'abiertas', 'cerradas'
 * @returns {Array} Cajas filtradas
 */
export const filtrarCajasPorEstado = (cajas, estado) => {
  if (estado === 'all') return cajas;
  if (estado === 'abiertas') return cajas.filter(c => c.estado?.toLowerCase() === 'abierta');
  if (estado === 'cerradas') return cajas.filter(c => c.estado?.toLowerCase() === 'cerrada');
  return cajas.filter(c => c.estado?.toLowerCase() === estado.toLowerCase());
};

/**
 * Filtrar cajas por usuario
 * @param {Array} cajas - Lista de cajas
 * @param {number} idUsuario - ID del usuario
 * @returns {Array} Cajas filtradas
 */
export const filtrarCajasPorUsuario = (cajas, idUsuario) => {
  if (!idUsuario) return cajas;
  return cajas.filter(c => c.idUsuario === idUsuario);
};

/**
 * Filtrar cajas por rango de fechas
 * @param {Array} cajas - Lista de cajas
 * @param {Date} fechaInicio - Fecha de inicio
 * @param {Date} fechaFin - Fecha de fin
 * @returns {Array} Cajas filtradas
 */
export const filtrarCajasPorFecha = (cajas, fechaInicio, fechaFin) => {
  if (!fechaInicio || !fechaFin) return cajas;
  
  const inicio = new Date(fechaInicio);
  const fin = new Date(fechaFin);
  fin.setHours(23, 59, 59, 999); // Fin del día
  
  return cajas.filter(c => {
    try {
      const fechaCaja = new Date(c.fechaApertura || 0);
      return fechaCaja >= inicio && fechaCaja <= fin;
    } catch {
      return false;
    }
  });
};

/**
 * Ordenar cajas por diferentes criterios
 * @param {Array} cajas - Lista de cajas
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Cajas ordenadas
 */
export const ordenarCajas = (cajas, criterio = 'fechaApertura_desc') => {
  const cajasCopy = [...cajas];
  
  switch (criterio) {
    case 'fechaApertura_desc':
      return cajasCopy.sort((a, b) => new Date(b.fechaApertura || 0) - new Date(a.fechaApertura || 0));
    case 'fechaApertura':
      return cajasCopy.sort((a, b) => new Date(a.fechaApertura || 0) - new Date(b.fechaApertura || 0));
    case 'montoInicial_desc':
      return cajasCopy.sort((a, b) => (b.montoInicial || 0) - (a.montoInicial || 0));
    case 'montoInicial':
      return cajasCopy.sort((a, b) => (a.montoInicial || 0) - (b.montoInicial || 0));
    case 'estado':
      return cajasCopy.sort((a, b) => (a.estado || '').localeCompare(b.estado || ''));
    case 'usuario':
      return cajasCopy.sort((a, b) => (a.usuarioNombre || '').localeCompare(b.usuarioNombre || ''));
    default:
      return cajasCopy;
  }
};

/**
 * Buscar cajas por término
 * @param {Array} cajas - Lista de cajas
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Cajas filtradas
 */
export const buscarCajasPorTermino = (cajas, termino) => {
  if (!termino || termino.trim() === '') {
    return cajas;
  }
  
  const terminoLower = termino.toLowerCase();
  return cajas.filter(caja => 
    caja.idCategoria?.toString().includes(terminoLower) ||
    formatearMonto(caja.montoInicial)?.toLowerCase().includes(terminoLower) ||
    formatearMonto(caja.montoFinal)?.toLowerCase().includes(terminoLower) ||
    formatearObservaciones(caja.observaciones, 500)?.toLowerCase().includes(terminoLower) ||
    convertirEstadoATexto(caja.estado)?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Obtener estadísticas de cajas
 * @param {Array} cajas - Lista de cajas
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasCajas = (cajas) => {
  const abiertas = cajas.filter(c => c.estado?.toLowerCase() === 'abierta').length;
  const cerradas = cajas.filter(c => c.estado?.toLowerCase() === 'cerrada').length;
  
  // Totales monetarios
  const totalMontoInicial = cajas.reduce((sum, c) => sum + (c.montoInicial || 0), 0);
  const totalMontoFinal = cajas.reduce((sum, c) => sum + (c.montoFinal || 0), 0);
  const totalVentas = cajas.reduce((sum, c) => sum + (c.totalVentas || 0), 0);
  const totalServicios = cajas.reduce((sum, c) => sum + (c.totalServicios || 0), 0);
  
  // Cajas con observaciones
  const conObservaciones = cajas.filter(c => c.observaciones && c.observaciones.trim() !== '').length;
  
  // Duración promedio de cajas cerradas
  const cajasCerradas = cajas.filter(c => c.estado?.toLowerCase() === 'cerrada' && c.fechaApertura && c.fechaCierre);
  let duracionPromedio = 0;
  
  if (cajasCerradas.length > 0) {
    const duraciones = cajasCerradas.map(c => {
      const apertura = new Date(c.fechaApertura);
      const cierre = new Date(c.fechaCierre);
      return cierre.getTime() - apertura.getTime();
    });
    
    duracionPromedio = Math.round(duraciones.reduce((a, b) => a + b, 0) / duraciones.length / (1000 * 60)); // en minutos
  }
  
  // Cajas del día actual
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const manana = new Date(hoy);
  manana.setDate(manana.getDate() + 1);
  
  const cajasHoy = cajas.filter(c => {
    try {
      const fechaCaja = new Date(c.fechaApertura || 0);
      return fechaCaja >= hoy && fechaCaja < manana;
    } catch {
      return false;
    }
  }).length;
  
  return {
    total: cajas.length,
    abiertas,
    cerradas,
    porcentajeAbiertas: cajas.length > 0 ? Math.round((abiertas / cajas.length) * 100) : 0,
    porcentajeCerradas: cajas.length > 0 ? Math.round((cerradas / cajas.length) * 100) : 0,
    totalMontoInicial,
    totalMontoFinal,
    totalVentas,
    totalServicios,
    totalGeneral: totalVentas + totalServicios,
    conObservaciones,
    porcentajeConObservaciones: cajas.length > 0 ? Math.round((conObservaciones / cajas.length) * 100) : 0,
    duracionPromedio,
    cajasHoy
  };
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar cajas a Excel
 * @param {Array} cajas - Lista de cajas
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcelCajas = (cajas, filename = 'cajas') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    // Importar dinámicamente para evitar problemas de SSR
    const XLSX = require('xlsx');
    
    // Crear un nuevo libro de trabajo
    const wb = XLSX.utils.book_new();
    
    // Preparar los datos
    const data = cajas.map(caja => ({
      'ID': caja.idCaja || '',
      'Fecha Apertura': formatearFechaCaja(caja.fechaApertura, true),
      'Fecha Cierre': formatearFechaCaja(caja.fechaCierre, true),
      'Duración': calcularDuracionCaja(caja.fechaApertura, caja.fechaCierre),
      'Monto Inicial': formatearMonto(caja.montoInicial),
      'Monto Final': formatearMonto(caja.montoFinal),
      'Total Ventas': formatearMonto(caja.totalVentas),
      'Total Servicios': formatearMonto(caja.totalServicios),
      'Diferencia': formatearMonto(calcularDiferenciaCaja(caja)),
      'Estado': convertirEstadoATexto(caja.estado),
      'ID Usuario': caja.idUsuario || '',
      'Observaciones': formatearObservaciones(caja.observaciones, 200)
    }));
    
    // Convertir a hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Establecer anchos de columna
    const wscols = [
      { wch: 8 },   // ID
      { wch: 25 },  // Fecha Apertura
      { wch: 25 },  // Fecha Cierre
      { wch: 15 },  // Duración
      { wch: 15 },  // Monto Inicial
      { wch: 15 },  // Monto Final
      { wch: 15 },  // Total Ventas
      { wch: 15 },  // Total Servicios
      { wch: 15 },  // Diferencia
      { wch: 15 },  // Estado
      { wch: 12 },  // ID Usuario
      { wch: 50 }   // Observaciones
    ];
    ws['!cols'] = wscols;
    
    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Cajas');
    
    // Agregar hoja de estadísticas
    const estadisticas = obtenerEstadisticasCajas(cajas);
    const statsData = [
      ['ESTADÍSTICAS DE CAJAS', ''],
      ['Total Cajas', estadisticas.total],
      ['Cajas Abiertas', estadisticas.abiertas],
      ['Cajas Cerradas', estadisticas.cerradas],
      ['% Abiertas', `${estadisticas.porcentajeAbiertas}%`],
      ['% Cerradas', `${estadisticas.porcentajeCerradas}%`],
      ['Cajas Hoy', estadisticas.cajasHoy],
      ['', ''],
      ['RESUMEN MONETARIO', ''],
      ['Total Monto Inicial', formatearMonto(estadisticas.totalMontoInicial)],
      ['Total Monto Final', formatearMonto(estadisticas.totalMontoFinal)],
      ['Total Ventas', formatearMonto(estadisticas.totalVentas)],
      ['Total Servicios', formatearMonto(estadisticas.totalServicios)],
      ['Total General', formatearMonto(estadisticas.totalGeneral)],
      ['', ''],
      ['ESTADÍSTICAS ADICIONALES', ''],
      ['Con Observaciones', estadisticas.conObservaciones],
      ['% Con Observaciones', `${estadisticas.porcentajeConObservaciones}%`],
      ['Duración Promedio', `${estadisticas.duracionPromedio} minutos`]
    ];
    
    const wsStats = XLSX.utils.aoa_to_sheet(statsData);
    XLSX.utils.book_append_sheet(wb, wsStats, 'Estadísticas');
    
    // Generar nombre de archivo con fecha
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    // Guardar el archivo
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Archivo ${nombreArchivo} generado exitosamente`,
      count: cajas.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando cajas a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar cajas a PDF
 * @param {Array} cajas - Lista de cajas
 * @param {string} title - Título del documento
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDFCajas = (cajas, title = 'Reporte de Cajas') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    // Importar jsPDF y el plugin
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');
    
    // Crear nuevo documento PDF
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Configuración del documento
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Logo/Encabezado
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageWidth / 2, 15, { align: 'center' });
    
    // Subtítulo con fecha
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generado: ${fechaStr}`, pageWidth / 2, 22, { align: 'center' });
    doc.text(`Total de cajas: ${cajas.length}`, pageWidth / 2, 28, { align: 'center' });
    
    // Estadísticas
    doc.setFontSize(12);
    doc.text('Resumen Estadístico', 14, 40);
    
    const estadisticas = obtenerEstadisticasCajas(cajas);
    const resumen = [
      `Total de cajas: ${estadisticas.total}`,
      `Cajas abiertas: ${estadisticas.abiertas} (${estadisticas.porcentajeAbiertas}%)`,
      `Cajas cerradas: ${estadisticas.cerradas} (${estadisticas.porcentajeCerradas}%)`,
      `Total monto inicial: ${formatearMonto(estadisticas.totalMontoInicial)}`,
      `Total monto final: ${formatearMonto(estadisticas.totalMontoFinal)}`,
      `Cajas de hoy: ${estadisticas.cajasHoy}`
    ];
    
    doc.setFontSize(10);
    resumen.forEach((linea, index) => {
      doc.text(`• ${linea}`, 16, 48 + (index * 5));
    });
    
    // Preparar datos para la tabla
    const tableData = cajas.map(caja => [
      caja.idCaja || '',
      formatearFechaCaja(caja.fechaApertura),
      formatearFechaCaja(caja.fechaCierre),
      calcularDuracionCaja(caja.fechaApertura, caja.fechaCierre),
      formatearMonto(caja.montoInicial),
      formatearMonto(caja.montoFinal),
      formatearMonto(caja.totalVentas),
      formatearMonto(caja.totalServicios),
      convertirEstadoATexto(caja.estado)
    ]);
    
    // Crear tabla
    doc.autoTable({
      startY: 75,
      head: [['ID', 'Apertura', 'Cierre', 'Duración', 'Inicial', 'Final', 'Ventas', 'Servicios', 'Estado']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [79, 70, 229], // Indigo
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 20 },
        8: { cellWidth: 20 }
      },
      margin: { top: 75 },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak'
      },
      didDrawPage: function(data) {
        // Pie de página
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Página ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          'SYSTEM JJ',
          pageWidth - 10,
          pageHeight - 10,
          { align: 'right' }
        );
      }
    });
    
    // Generar nombre de archivo con fecha
    const fechaArchivo = fecha.toISOString().split('T')[0];
    const nombreArchivo = `reporte_cajas_${fechaArchivo}.pdf`;
    
    // Guardar el archivo
    doc.save(nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Documento ${nombreArchivo} generado exitosamente`,
      count: cajas.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando cajas a PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo (Excel + PDF)
 * @param {Array} cajas - Lista de cajas
 * @param {string} baseFilename - Nombre base del archivo (sin extensión)
 * @returns {Promise} Resultado de la exportación
 */
export const exportarReporteCompletoCajas = async (cajas, baseFilename = 'reporte_completo_cajas') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (cajas.length === 0) {
      throw new Error('No hay datos para exportar');
    }
    
    // Exportar Excel
    const excelResult = exportarExcelCajas(cajas, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    // Pequeño delay para evitar conflictos
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Exportar PDF
    const pdfResult = exportarPDFCajas(cajas, `Reporte de Cajas - ${baseFilename}`);
    if (!pdfResult.success) {
      throw new Error(`Error en PDF: ${pdfResult.error}`);
    }
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: cajas.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo de cajas:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API
  obtenerCajas,
  obtenerCajaPorId,
  obtenerResumenCaja,
  obtenerCajaAbiertaPorUsuario,
  obtenerCajasPorUsuario,
  obtenerCajasPorRangoFechas,
  obtenerTotalCajaActual,
  crearCaja,
  abrirCaja,
  cerrarCaja,
  actualizarCaja,
  eliminarCaja,
  cambiarEstadoCaja,
  obtenerEstadosCajas,
  buscarCajas,
  
  // Funciones auxiliares
  formatearFechaCaja,
  calcularDuracionCaja,
  convertirEstadoATexto,
  esCajaAbierta,
  formatearMonto,
  calcularTotalCaja,
  calcularDiferenciaCaja,
  formatearObservaciones,
  filtrarCajasPorEstado,
  filtrarCajasPorUsuario,
  filtrarCajasPorFecha,
  ordenarCajas,
  buscarCajasPorTermino,
  obtenerEstadisticasCajas,
  
  // Funciones de exportación
  exportarExcelCajas,
  exportarPDFCajas,
  exportarReporteCompletoCajas
};